import { useId, useMemo } from 'react';
import { Input, Select } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useCitySearch, useCountries, usePostalSearch, useRegions } from '@/hooks/useGeo';
import { aplicarCodigoPostal, type CamposDireccion, type PostalLookup } from '@/lib/geo-form';
import { paisFueraDelCatalogo, provinciaDesdeCiudad } from '@/lib/geo-form';

/**
 * País, provincia y ciudad de una ficha.
 *
 * - **País**: lista cerrada del catálogo (ISO 3166-1). El texto libre antiguo se sigue guardando en
 *   su columna, pero lo que manda es el código.
 * - **Provincia**: lista que depende del país. Si el país no tiene provincias en el catálogo, el
 *   campo pasa a texto libre (o a un aviso, con `allowText={false}`, en fichas sin columna de texto).
 * - **Ciudad**: se escribe y se sugieren las del catálogo. Se permite guardar una que no esté: hay
 *   pedanías y direcciones extranjeras que no aparecen en ninguna lista.
 */

export function CountrySelect({ value, onChange, id }: {
  value: string;
  onChange: (code: string) => void;
  id?: string;
}) {
  const { data, isLoading } = useCountries();
  const paises = data?.data ?? [];
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)} id={id} disabled={isLoading}>
      <option value="">{isLoading ? 'Cargando países…' : 'Sin país'}</option>
      {/* Un código que no está en el catálogo se muestra tal cual: si no, el desplegable diría «Sin
          país» mientras el formulario guarda otra cosa. */}
      {!isLoading && paisFueraDelCatalogo(value, paises) ? (
        <option value={value}>{value} · no está en el catálogo</option>
      ) : null}
      {paises.map((pais) => (
        <option key={pais.code} value={pais.code}>
          {pais.nameEs}{pais.euMember ? ' · UE' : ''}
        </option>
      ))}
    </Select>
  );
}

export function ProvinceSelect({ countryCode, value, textValue, allowText = true, onChange }: {
  countryCode: string;
  value: string;
  /** Texto libre de la provincia, para países sin provincias en el catálogo. */
  textValue?: string;
  /** `false` cuando la ficha no tiene dónde guardar el texto (proveedor): se avisa en vez de pintar un campo muerto. */
  allowText?: boolean;
  /**
   * Devuelve el código (vacío si el país no tiene catálogo) y el nombre: la ficha conserva el texto
   * de la provincia para etiquetas y PDFs.
   */
  onChange: (code: string, name: string) => void;
}) {
  const { data, isLoading } = useRegions(countryCode || null);
  const provincias = data?.data ?? [];
  // Sin provincias en el catálogo (la mayoría de países fuera de Europa) el campo pasa a texto
  // libre: dejar el hueco vacío impedía escribir una dirección extranjera con provincia.
  if (countryCode && !isLoading && provincias.length === 0) {
    if (!allowText) {
      return (
        <p className="px-1 py-2 text-[11px] text-muted-foreground">
          Este país no tiene provincias en el catálogo.
        </p>
      );
    }
    return (
      <Input
        value={textValue ?? ''}
        // El texto no es un código: se guarda en la columna de texto y `region_code` queda vacío.
        onChange={(event) => onChange('', event.target.value)}
        placeholder="Provincia o estado"
      />
    );
  }
  return (
    <Select
      value={value}
      onChange={(event) => {
        const elegida = provincias.find((provincia) => provincia.code === event.target.value);
        onChange(event.target.value, elegida?.name ?? '');
      }}
      disabled={!countryCode || isLoading}
    >
      <option value="">
        {!countryCode ? 'Elige primero el país' : isLoading ? 'Cargando provincias…' : 'Sin provincia'}
      </option>
      {provincias.map((provincia) => (
        <option key={provincia.code} value={provincia.code}>{provincia.name}</option>
      ))}
    </Select>
  );
}

/**
 * Ciudad con sugerencias del catálogo. El valor guardado es el **texto** (como hasta ahora) y, si
 * acierta con una del catálogo, se avisa de la provincia para que no se quede sin rellenar.
 */
export function CityField({ countryCode, value, regionCode, onChange, onPickProvince, placeholder }: {
  countryCode: string;
  value: string;
  /** Provincia puesta ahora mismo: si ya hay una elegida, la ciudad no la pisa. */
  regionCode?: string;
  onChange: (texto: string) => void;
  /** Al elegir una ciudad del catálogo se propone su provincia (código y nombre). */
  onPickProvince?: (regionCode: string, regionName: string) => void;
  placeholder?: string;
}) {
  const listId = useId();
  const { data, isFetching } = useCitySearch(countryCode || null, value);
  // El nombre de la provincia sale del catálogo del país: `cities.province_code` no garantiza que
  // el código exista, y guardar uno que no esté daría 400 al guardar la ficha.
  const { data: regionesData } = useRegions(countryCode || null);
  const ciudades = useMemo(() => data?.data ?? [], [data]);
  const provincias = regionesData?.data ?? [];

  const aplicarProvincia = (nombreCiudad: string) => {
    const provincia = provinciaDesdeCiudad(ciudades, provincias, nombreCiudad, regionCode);
    if (provincia) onPickProvince?.(provincia.code, provincia.name);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => aplicarProvincia(event.target.value)}
        placeholder={placeholder ?? 'Ciudad'}
        list={listId}
        autoComplete="off"
      />
      <datalist id={listId}>
        {ciudades.map((ciudad) => (
          <option key={ciudad.id} value={ciudad.name} />
        ))}
      </datalist>
      {isFetching ? (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
          buscando…
        </span>
      ) : null}
    </div>
  );
}

/**
 * Código postal con búsqueda en el catálogo.
 *
 * Al salir del campo se pregunta por el código y se avisa hacia arriba
 * con **lo que hay que rellenar**: la provincia siempre (la determina el código), el municipio solo
 * si la ciudad está vacía. Si el código no está en el catálogo —o el país no tiene catálogo— no se
 * dice nada: el código se guarda como texto y la ficha sigue siendo válida.
 */
export function PostalCodeField({ countryCode, value, city, onChange, onApply }: {
  countryCode: string;
  value: string;
  /** Ciudad puesta ahora mismo: si ya hay una escrita, el código postal no la pisa. */
  city: string;
  onChange: (texto: string) => void;
  /** Campos que hay que rellenar; vacío cuando el código no está en el catálogo. */
  onApply: (cambios: Partial<CamposDireccion>) => void;
}) {
  const listId = useId();
  // Sin catálogo para el país (todo menos España, hoy) no se pregunta nada: el código es texto libre.
  const { data: paisesData } = useCountries();
  const hayCatalogo = Boolean(paisesData?.data.find((pais) => pais.code === countryCode)?.hasPostalCodes);
  const { data } = usePostalSearch(hayCatalogo ? countryCode : null, value);
  const { data: regionesData } = useRegions(countryCode || null);
  const sugerencias = useMemo(() => data?.data ?? [], [data]);

  const consultar = async (texto: string) => {
    let codigo = texto.trim();
    // Escribir «4001» en España es el código 04001: el cero a la izquierda se ha perdido al teclear.
    if (countryCode === 'ES' && /^\d{4}$/.test(codigo)) codigo = `0${codigo}`;
    if (!hayCatalogo || !/^\d{4,10}$/.test(codigo)) {
      onApply({});
      return;
    }
    // El valor normalizado es el que se guarda: si no, la ficha guarda «4001» y el catálogo tiene 04001.
    if (codigo !== texto.trim()) onChange(codigo);
    let hallazgo: PostalLookup | null = null;
    try {
      const respuesta = await api<{ data: PostalLookup }>(
        `/geo/postal-codes/${encodeURIComponent(codigo)}?countryCode=${countryCode}`
      );
      hallazgo = respuesta.data;
    } catch {
      // 404: el código no está en el catálogo. No es un error de la pantalla.
      hallazgo = null;
    }
    onApply(aplicarCodigoPostal({ city, regionCode: '', province: '' }, hallazgo, regionesData?.data ?? []));
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={(event) => void consultar(event.target.value)}
        placeholder="Código postal"
        list={listId}
        autoComplete="off"
        inputMode="numeric"
      />
      <datalist id={listId}>
        {sugerencias.map((sugerencia) => (
          <option key={sugerencia.code} value={sugerencia.code}>
            {sugerencia.municipality}
          </option>
        ))}
      </datalist>
    </div>
  );
}
